import { useState } from "react";
import { Form, useNotify, useTranslate } from "ra-core";

import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { nativeSelectClass } from "@/tenax/tasks/taskData";
import { HolderFields, NoReferralCodeWarning } from "./HolderFields";
import { useHolderValues } from "./racketHooks";
import {
  RACKET_STATUSES,
  useLoanPurposes,
  useRacketCommand,
  type RacketStatus,
  type TestRacket,
} from "./racketData";

export type RacketAction = "transfer" | "status" | "correction";

// The movements of one racket besides loan and return:
//   transfer: from the holder to another, on a new loan;
//   status: maintenance, lost, retired or back in the house;
//   correction: CRM and reality apart, any status and holder, note required.
export const RacketActionDialog = ({
  racket,
  action,
  onClose,
}: {
  racket: TestRacket;
  action: RacketAction;
  onClose: () => void;
}) => {
  const translate = useTranslate();
  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>
            {translate(`tenax.rackets.actions.${action}`)} · {racket.code}
          </DialogTitle>
          <DialogDescription>
            {translate(`tenax.rackets.actions.${action}_hint`)}
          </DialogDescription>
        </DialogHeader>
        <Form
          defaultValues={{
            contact_id:
              action === "correction" ? racket.holder_contact_id : null,
            company_id:
              action === "correction" ? racket.holder_company_id : null,
          }}
        >
          <ActionBody racket={racket} action={action} onClose={onClose} />
        </Form>
      </DialogContent>
    </Dialog>
  );
};

const STATUS_CHOICES: Record<RacketAction, readonly RacketStatus[]> = {
  transfer: ["on_loan"],
  status: ["in_house", "maintenance", "lost", "retired"],
  correction: RACKET_STATUSES,
};

const ActionBody = ({
  racket,
  action,
  onClose,
}: {
  racket: TestRacket;
  action: RacketAction;
  onClose: () => void;
}) => {
  const translate = useTranslate();
  const notify = useNotify();
  const run = useRacketCommand();
  const { contactId, companyId } = useHolderValues();
  const { data: purposes = [] } = useLoanPurposes();
  const activePurposes = purposes.filter((purpose) => purpose.active);
  const choices = STATUS_CHOICES[action];
  const [status, setStatus] = useState<RacketStatus>(
    action === "correction" ? racket.status : choices[0],
  );
  const [purposeId, setPurposeId] = useState<number | null>(null);
  const [lentAt, setLentAt] = useState("");
  const [expected, setExpected] = useState("");
  const [note, setNote] = useState("");
  const [saving, setSaving] = useState(false);
  const purpose = purposeId ?? activePurposes[0]?.id ?? null;
  const toLoan = status === "on_loan";
  const sameHolder =
    racket.status === "on_loan" &&
    contactId === racket.holder_contact_id &&
    companyId === racket.holder_company_id;
  const needsNote = action === "correction";

  const submit = async () => {
    if (toLoan && contactId == null && companyId == null) {
      notify("tenax.rackets.need_holder", { type: "error" });
      return;
    }
    if (needsNote && !note.trim()) {
      notify("tenax.rackets.actions.need_note", { type: "error" });
      return;
    }
    setSaving(true);
    const note_ = note.trim() || null;
    const [name, args] =
      action === "transfer"
        ? [
            "tenax_transfer_test_racket",
            {
              p_racket_id: racket.id,
              p_contact_id: contactId,
              p_company_id: companyId,
              p_purpose_id: purpose,
              p_expected_return_at: expected || null,
              p_note: note_,
            },
          ]
        : action === "status"
          ? [
              "tenax_set_test_racket_status",
              { p_racket_id: racket.id, p_status: status, p_note: note_ },
            ]
          : [
              "tenax_correct_test_racket",
              {
                p_racket_id: racket.id,
                p_status: status,
                p_note: note_,
                p_contact_id: toLoan ? contactId : null,
                p_company_id: toLoan ? companyId : null,
                p_purpose_id: toLoan && !sameHolder ? purpose : null,
                p_lent_at: toLoan && !sameHolder && lentAt ? lentAt : null,
                p_expected_return_at: toLoan && expected ? expected : null,
              },
            ];
    const ok = await run(name, args, "tenax.rackets.actions.done");
    setSaving(false);
    if (ok) onClose();
  };

  return (
    <div className="flex flex-col gap-4">
      {choices.length > 1 ? (
        <div className="flex flex-col gap-1.5">
          <Label htmlFor="racket-action-status">
            {translate("tenax.rackets.fields.status")}
          </Label>
          <select
            id="racket-action-status"
            className={nativeSelectClass}
            value={status}
            onChange={(event) => setStatus(event.target.value as RacketStatus)}
          >
            {choices.map((value) => (
              <option key={value} value={value}>
                {translate(`tenax.rackets.status.${value}`)}
              </option>
            ))}
          </select>
        </div>
      ) : null}
      {toLoan ? (
        <>
          <HolderFields />
          <NoReferralCodeWarning contactId={contactId} companyId={companyId} />
          {!sameHolder ? (
            <div className="grid gap-3 md:grid-cols-2">
              <div className="flex flex-col gap-1.5">
                <Label htmlFor="racket-action-purpose">
                  {translate("tenax.rackets.fields.purpose_id")}
                </Label>
                <select
                  id="racket-action-purpose"
                  className={nativeSelectClass}
                  value={purpose ?? ""}
                  onChange={(event) => setPurposeId(Number(event.target.value))}
                >
                  {activePurposes.map((item) => (
                    <option key={item.id} value={item.id}>
                      {item.name}
                    </option>
                  ))}
                </select>
              </div>
              {action === "correction" ? (
                <div className="flex flex-col gap-1.5">
                  <Label htmlFor="racket-action-lent-at">
                    {translate("tenax.rackets.fields.lent_at")}
                  </Label>
                  <Input
                    id="racket-action-lent-at"
                    type="date"
                    value={lentAt}
                    onChange={(event) => setLentAt(event.target.value)}
                  />
                </div>
              ) : null}
            </div>
          ) : null}
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="racket-action-expected">
              {translate("tenax.rackets.fields.expected_return_at")}
            </Label>
            <Input
              id="racket-action-expected"
              type="date"
              value={expected}
              onChange={(event) => setExpected(event.target.value)}
            />
          </div>
        </>
      ) : null}
      <div className="flex flex-col gap-1.5">
        <Label htmlFor="racket-action-note">
          {translate(
            needsNote
              ? "tenax.rackets.fields.note_required"
              : "tenax.rackets.fields.note",
          )}
        </Label>
        <Textarea
          id="racket-action-note"
          value={note}
          onChange={(event) => setNote(event.target.value)}
        />
      </div>
      <DialogFooter>
        <Button type="button" variant="outline" onClick={onClose}>
          {translate("ra.action.cancel")}
        </Button>
        <Button
          type="button"
          disabled={saving || (toLoan && !sameHolder && purpose == null)}
          onClick={submit}
        >
          {translate("ra.action.confirm")}
        </Button>
      </DialogFooter>
    </div>
  );
};
